import { useState } from 'react';

import { useQuery, useMutation } from '@apollo/client/react';
import { StatsCard } from '../components/StatsCard';
import { DateWithRelative } from '../components/DateWithRelative';
import {gql} from 'types/__generated__';

const DUPLICATE_USERS_QUERY = gql(`
  query DuplicateUsers {
    duplicateUsers {
      email
      currentLookupPointsTo
      oldUser {
        userId
        username
        name
        eventCount
        hasPassword
        createdAt
        organisationNames
      }
      newUser {
        userId
        username
        name
        eventCount
        hasPassword
        createdAt
        organisationNames
      }
    }
  }
`);

const RENAME_DUPLICATE_USER = gql(`
  mutation RenameDuplicateUser($userId: UUID!, $newEmail: String!) {
    renameDuplicateUser(userId: $userId, newEmail: $newEmail)
  }
`);

interface DuplicateUserInfo {
  userId: string;
  username: string;
  name: string;
  eventCount: number;
  hasPassword: boolean;
  createdAt: number;
  organisationNames: string[];
}

interface DuplicateUserPair {
  email: string;
  currentLookupPointsTo: string;
  oldUser: DuplicateUserInfo;
  newUser: DuplicateUserInfo;
}

function UserCard({ user, label, isActive, isLessActive, onRename }: {
  user: DuplicateUserInfo;
  label: string;
  isActive: boolean;
  isLessActive: boolean;
  onRename?: () => void;
}) {
  return (
    <div className={`flex-1 rounded-lg border p-4 ${
      isActive
        ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/10'
        : isLessActive
          ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
        <div className="flex items-center gap-2">
          {isActive && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 font-medium">
              Current Lookup
            </span>
          )}
          {isLessActive && onRename && (
            <button
              onClick={onRename}
              className="text-xs px-3 py-1 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors font-medium"
            >
              Rename to disable
            </button>
          )}
        </div>
      </div>

      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Username</span>
          <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">{user.username}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Name</span>
          <span className="text-gray-900 dark:text-gray-100">{user.name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Events</span>
          <span className={`font-semibold ${user.eventCount > 1 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
            {user.eventCount}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Has Password</span>
          <span>{user.hasPassword ? '✓' : '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500 dark:text-gray-400">Created</span>
          <DateWithRelative timestamp={user.createdAt} />
        </div>
        <div className="flex justify-between items-start">
          <span className="text-gray-500 dark:text-gray-400">Orgs</span>
          <div className="text-right">
            {user.organisationNames.length > 0
              ? user.organisationNames.map((name, i) => (
                  <div key={i} className="text-gray-900 dark:text-gray-100">{name}</div>
                ))
              : <span className="text-gray-400">None</span>
            }
          </div>
        </div>
        <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
          <span className="text-gray-400 dark:text-gray-500 font-mono text-xs break-all">{user.userId}</span>
        </div>
      </div>
    </div>
  );
}

export default function DuplicateUsersPage() {
  const { loading, error, data, refetch } = useQuery(DUPLICATE_USERS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [renameMutation, { loading: renaming }] = useMutation(RENAME_DUPLICATE_USER, {
    onCompleted: () => refetch(),
    onError: (err) => alert(`Failed to rename: ${err.message}`),
  });

  const [confirmingRename, setConfirmingRename] = useState<{ userId: string; email: string } | null>(null);

  const duplicates: DuplicateUserPair[] = data?.duplicateUsers ?? [];

  const handleRename = (userId: string, email: string) => {
    setConfirmingRename({ userId, email });
  };

  const confirmRename = () => {
    if (!confirmingRename) return;
    const newEmail = `duplicate-${confirmingRename.userId.slice(0, 8)}@disabled.cway.se`;
    renameMutation({ variables: { userId: confirmingRename.userId, newEmail } });
    setConfirmingRename(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading duplicate users</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Duplicate Users</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Users created multiple times due to readmodel race conditions. Review and rename the less active duplicate.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatsCard label="Duplicate Pairs" value={loading ? '...' : duplicates.length} />
        <StatsCard
          label="Status"
          value={loading ? 'Loading...' : duplicates.length === 0 ? 'Clean' : 'Needs Review'}
          valueColor={duplicates.length === 0 ? 'text-green-600' : 'text-orange-600'}
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      <div className="space-y-6">
        {duplicates.map((pair) => {
          const oldIsOrphaned = pair.currentLookupPointsTo !== pair.oldUser.userId;
          const newIsOrphaned = pair.currentLookupPointsTo !== pair.newUser.userId;

          return (
            <div key={pair.email} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 font-mono">{pair.email}</h3>
              </div>

              <div className="flex gap-4">
                <UserCard
                  user={pair.oldUser}
                  label="First Created"
                  isActive={!oldIsOrphaned}
                  isLessActive={oldIsOrphaned}
                  onRename={oldIsOrphaned ? () => handleRename(pair.oldUser.userId, pair.email) : undefined}
                />
                <UserCard
                  user={pair.newUser}
                  label="Second Created"
                  isActive={!newIsOrphaned}
                  isLessActive={newIsOrphaned}
                  onRename={newIsOrphaned ? () => handleRename(pair.newUser.userId, pair.email) : undefined}
                />
              </div>
            </div>
          );
        })}
      </div>

      {!loading && duplicates.length === 0 && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No duplicate users detected.
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmingRename && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Confirm Rename</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              This will rename the duplicate user's email to:
            </p>
            <p className="text-sm font-mono bg-gray-100 dark:bg-gray-700 p-2 rounded mb-4">
              duplicate-{confirmingRename.userId.slice(0, 8)}@disabled.cway.se
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              The user data will be preserved but the account will no longer conflict with the active user.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmingRename(null)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRename}
                disabled={renaming}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {renaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
